// src/features/control/components/ControlPage.tsx
//
// «Control page» (w7.u5, CF S6 "Control": device health, incidents) — route
// /control. `GET /api/v2/ui/control` (w7.u1) returns every registered `it.*`
// id (02-metric-catalogue.md S2.5) for one property and period, grouped here
// into three sections that follow the catalogue's own audience note (device
// health, incidents, and the ingest/technical-reconciliation signals it also
// feeds to the status page and the SLA chapter) — not a bespoke chart per id.
import React from 'react';
import { Box } from '@mui/material';
import { useDataProvider, useLocaleState, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { MetricPanelSection } from '@/features/insight-v2-shared/components/MetricPanelSection';
import { metricExportText, metricLabel } from '@/lib/v2MetricView';
import type { MetricResultDto } from '@/types/analyticsV2';

const GROUPS: { key: string; sectionKey: string; ids: string[] }[] = [
  {
    key: 'deviceHealth',
    sectionKey: 'resources.control.sections.deviceHealth',
    ids: [
      'it.availability', 'it.unknown_share', 'it.silent_devices', 'it.down_time',
      'it.peripheral_faults', 'it.consumables_low', 'it.pms_link', 'it.software_versions',
      'it.remote_sessions', 'it.mode_changes',
    ],
  },
  {
    key: 'incidents',
    sectionKey: 'resources.control.sections.incidents',
    ids: ['it.incidents', 'it.time_to_resolve_median', 'it.time_to_acknowledge_median', 'it.detection_lag'],
  },
  {
    key: 'ingest',
    sectionKey: 'resources.control.sections.ingest',
    ids: ['it.feed_freshness', 'it.reconciliation_gap', 'it.unmatched_journeys', 'it.ingest_gaps', 'it.rejected_events'],
  },
];

export const ControlPage: React.FC = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const dataProvider = useDataProvider();
  const property = useV2PropertySelection('control');
  const exportCsv = useCsvExport({ page: 'control' });
  const canExport = hasResourcePermission('control', 'export');

  const { propertyId } = property;
  const [period, setPeriod] = useAnalyticsPeriod();

  const { data, loading, error, refresh } = useAnalyticsFetch<MetricResultDto[]>(
    () => (dataProvider as any).getAnalyticsControl({ property: propertyId, from: period.from, to: period.to }),
    [dataProvider, propertyId, period.from, period.to],
    Boolean(propertyId)
  );

  const byId = React.useMemo(() => {
    const map = new Map<string, MetricResultDto>();
    (data ?? []).forEach((m) => map.set(m.id, m));
    return map;
  }, [data]);

  const groupedIds = new Set(GROUPS.flatMap((g) => g.ids));
  const otherMetrics = (data ?? []).filter((m) => !groupedIds.has(m.id));

  const handleExport = async () => {
    if (!data) return;
    await exportCsv(
      data,
      [
        { labelKey: 'resources.insightV2.export.metric', value: (r: MetricResultDto) => metricLabel(r, translate) },
        { labelKey: 'resources.insightV2.export.value', value: (r: MetricResultDto) => metricExportText(r, translate, locale) },
      ],
      { from: period.from, to: period.to }
    );
  };

  return (
    <Box>
      <SectionHeader manualScreen="control"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
          </Box>
        }
        subtitle={translate('resources.control.subtitle')}
        helpConfig={{
          titleKey: 'resources.control.help.title',
          descriptionKey: 'resources.control.help.description',
          featureKeys: ['resources.control.help.features.list'],
          tipKeys: ['resources.control.help.tips.property'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {!propertyId ? (
        <PropertyState selection={property} />
      ) : (
        <>
          {error && <FetchErrorBanner onRetry={refresh} />}
          {GROUPS.map((group) => (
            <MetricPanelSection
              key={group.key}
              title={translate(group.sectionKey)}
              metrics={group.ids.map((id) => byId.get(id)).filter((m): m is MetricResultDto => Boolean(m))}
              loading={loading}
            />
          ))}
          <MetricPanelSection title={translate('resources.control.sections.other')} metrics={otherMetrics} loading={loading} />
        </>
      )}
    </Box>
  );
};

export default ControlPage;
